import {computed, inject, Injectable, OnDestroy, signal} from '@angular/core';
import {firstValueFrom, forkJoin, Observable, Subscription} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {StateStore, StateStorePromises, UserRepositoryService, UserSettings} from '@xmedical/core';
import {CacheService} from '../services/cache.service';
import {CoreEvent, CoreEventsService} from '../services/core-events.service';
import {AuthenticationService} from '../auth/authentication.service';

/**
 * UserStore
 *
 * Manages user-specific data including permissions and settings.
 * Uses Angular signals for reactive state management.
 *
 * Data managed:
 * - User permissions (array of permission strings)
 * - User settings (personal preferences, theme, etc)
 * - User ID
 *
 * All data is cached in localStorage with user ID prefix.
 * Each store registers its own boot steps for initialization tracking.
 */
@Injectable({
  providedIn: 'root'
})
export class UserStore implements StateStore, StateStorePromises, OnDestroy {
  private userRepo = inject(UserRepositoryService);
  private cache = inject(CacheService);
  events = inject(CoreEventsService);
  private auth = inject(AuthenticationService);

  private eventSubscription: Subscription;

  constructor() {
    // Listen for practice changes and reload data
    this.eventSubscription = this.events.events$.subscribe(event => {
      if (event.type === 'practice_change') {
        this.reloadForPracticeSwitch().subscribe();
      }
    });
  }

  ngOnDestroy(): void {
    this.eventSubscription?.unsubscribe();
  }

  // State signals
  readonly userId = signal<string>('public');
  readonly permissions = signal<string[]>([]);
  readonly settings = signal<UserSettings | null>(null);
  readonly loaded = signal<boolean>(false);
  readonly loading = signal<boolean>(false);

  // Promise for tracking in-flight load operation
  loadingPromise: Promise<void> | null = null;

  // Computed signals
  readonly hasPermissions = computed(() => this.permissions()?.length > 0);
  readonly isSuperuser = computed(() => this.permissions()?.includes('Superuser') ?? false);
  readonly firstName = computed(() => this.auth.getFirstName() ?? '');
  readonly surname = computed(() => this.auth.getSurname() ?? '');
  readonly email = computed(() => this.auth.getEmailAddress() ?? '');

  /**
   * Initialize user data
   * Loads permissions and settings in parallel
   *
   * @returns Observable that completes when all data is loaded
   */
  initialize(): Observable<void> {
    this.loading.set(true);

    // Set user ID from auth service
    const userId = this.auth.getUserId();
    this.userId.set(userId);

    // Load all data in parallel
    return forkJoin({
      settings: this.loadUserSettings(),
      permissions: this.loadUserPermissions()
    }).pipe(
      tap(() => {
        this.loaded.set(true);
        this.loading.set(false);
      }),
      map(() => void 0)
    );
  }

  /**
   * Reload user data for practice switch
   * Clears cache and reloads fresh data for the new practice context
   *
   * @returns Observable that completes when all data is reloaded
   */
  reloadForPracticeSwitch(): Observable<void> {
    this.loading.set(true);
    this.loaded.set(false);

    // Clear cached data for this user
    const userId = this.userId();
    this.cache.invalidate('current-user-settings', userId);
    this.cache.invalidate('current-user-permissions', userId);

    // Reload all data
    return forkJoin({
      settings: this.loadUserSettings(),
      permissions: this.loadUserPermissions()
    }).pipe(
      tap(() => {
        this.loaded.set(true);
        this.loading.set(false);
      }),
      map(() => void 0)
    );
  }

  /**
   * Load user settings
   * Uses cache-first strategy with no TTL, user-scoped cache key
   */
  private loadUserSettings(): Observable<UserSettings> {
    return this.cache.cacheFirst(
      'current-user-settings',
      () => this.userRepo.loadCurrentUserSettings(),
      {
        userId: this.userId(),
        onCacheHit: (settings) => {
          this.settings.set(settings);
          this.events.trigger(new CoreEvent('user_settings_loaded'));

        },
        onFetchComplete: (settings) => {
          this.settings.set(settings);
          this.events.trigger(new CoreEvent('user_settings_loaded'));

        }
      }
    );
  }

  /**
   * Load user permissions
   * Uses cache-first strategy with no TTL, user-scoped cache key
   */
  private loadUserPermissions(): Observable<string[]> {
    return this.cache.cacheFirst(
      'current-user-permissions',
      () => this.userRepo.loadCurrentUserPermissions(),
      {
        userId: this.userId(),
        onCacheHit: (permissions) => {
          this.permissions.set(permissions);
          this.events.trigger(new CoreEvent('permissions_loaded'));

        },
        onFetchComplete: (permissions) => {
          this.permissions.set(permissions);
          this.events.trigger(new CoreEvent('permissions_loaded'));

        }
      }
    );
  }

  /**
   * Update user settings
   *
   * @param settings - Updated settings
   * @returns Observable that completes when settings are saved
   */
  updateUserSettings(settings: UserSettings): Observable<boolean> {
    return this.userRepo.saveCurrentUserSettings(settings).pipe(
      tap(success => {
        if (success) {
          this.settings.set(settings);
          // Update cache
          this.cache.set(`${this.userId()}-current-user-settings`, settings);
          this.events.trigger(new CoreEvent('user_settings_changed'));
        }
      })
    );
  }

  /**
   * Ensure user data is loaded - on-demand loading for page refresh scenarios.
   * - If already loaded, returns immediately
   * - If loading in progress, waits for completion
   * - If not loaded, triggers fetch and waits
   *
   * @returns Promise that resolves when user data is available
   */
  async ensureLoaded(): Promise<void> {
    if (this.loaded()) {
      return;
    }

    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    // Not loaded and not loading - trigger initialization
    this.loadingPromise = firstValueFrom(this.initialize()).finally(() => {
      this.loadingPromise = null;
    });

    return this.loadingPromise;
  }

  /**
   * Check if user has a specific permission
   *
   * @param permission - Permission string to check
   * @returns True if user has the permission
   */
  hasPermission(permission: string): boolean {
    return this.permissions()?.includes(permission) ?? false;
  }

  /**
   * Clear user data (called on logout)
   */
  clearUserData(): void {
    this.permissions.set([]);
    this.settings.set(null);
    this.loaded.set(false);

    // Clear cache
    const userId = this.userId();
    this.cache.invalidate('current-user-settings', userId);
    this.cache.invalidate('current-user-permissions', userId);
  }
}
